import { Sim } from "../sim/sim";
import { Camera } from "./camera";
import { FxSystem } from "./fx";
import { drawSprite, drawIcon, paletteFor, type Palette } from "./art";
import { spriteAtlas } from "./sprites";
import { settings } from "../ui/settings";
import { getFaction, type Faction } from "../data";
import { KIND_UNIT, KIND_BUILDING, KIND_WRECKAGE, KIND_FEATURE, OWNER_PLAYER, PROJ_ARC } from "../sim/constants";
import { F_UNDER_CONSTRUCTION, ACT_BUILD, ACT_RECLAIM, ACT_REPAIR } from "../sim/world";
import { canPlace } from "../sim/placement";

export interface RenderView {
  selection: Set<number>;
  placing: { defId: string; x: number; y: number; snap: { magnetized: boolean; nx: number; ny: number } | null } | null;
  selBox: { x0: number; y0: number; x1: number; y1: number } | null;
  hovered: number;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  readonly camera: Camera;
  readonly fx = new FxSystem();
  private sim: Sim;
  private terrain: HTMLCanvasElement;
  private terrainSS = 2; // terrain supersample factor (crisp at zoom)
  private dpr = 1;
  private palPlayer: Palette;
  private palEnemy: Palette;
  private colPlayer: string;
  private colEnemy: string;
  private facPlayer: Faction;
  private facEnemy: Faction;

  constructor(canvas: HTMLCanvasElement, sim: Sim) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d")!;
    this.sim = sim;
    this.camera = new Camera(sim.world.gmap.worldW, sim.world.gmap.worldH);
    this.facPlayer = sim.cfg.playerFaction;
    this.facEnemy = sim.cfg.enemyFaction;
    this.palPlayer = paletteFor(this.facPlayer);
    this.palEnemy = paletteFor(this.facEnemy);
    this.colPlayer = getFaction(this.facPlayer).color;
    this.colEnemy = this.facEnemy === this.facPlayer ? "#ff4dd2" : getFaction(this.facEnemy).color;
    this.terrain = this.buildTerrain();
    // Start focused on the player's commander.
    const s = sim.world.gmap.def.starts.find((s) => s.owner === OWNER_PLAYER);
    if (s) this.camera.centerOn(s.x, s.y);
  }

  ownerColor(owner: number): string {
    return owner === OWNER_PLAYER ? this.colPlayer : owner === 2 ? this.colEnemy : "#8892a0";
  }
  ownerPalette(owner: number): Palette {
    return owner === OWNER_PLAYER ? this.palPlayer : this.palEnemy;
  }
  ownerFaction(owner: number): Faction {
    return owner === OWNER_PLAYER ? this.facPlayer : this.facEnemy;
  }

  resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.max(1, Math.floor(w * this.dpr));
    this.canvas.height = Math.max(1, Math.floor(h * this.dpr));
    this.canvas.style.width = w + "px";
    this.canvas.style.height = h + "px";
    this.camera.resize(w, h);
  }

  // --- Terrain: base tone + low-frequency tonal variation + fine grain, with
  // soft-edged contour bands and baked metal-spot craters. Supersampled so it
  // stays crisp when the camera zooms in. Built once. ---
  private buildTerrain(): HTMLCanvasElement {
    const m = this.sim.world.gmap;
    const ss = this.terrainSS;
    const W = m.worldW * ss;
    const H = m.worldH * ss;
    const c = document.createElement("canvas");
    c.width = W;
    c.height = H;
    const g = c.getContext("2d")!;

    const img = g.createImageData(W, H);
    const data = img.data;
    const hash = (x: number, y: number): number => {
      let h = (x * 374761393 + y * 668265263) & 0x7fffffff;
      h = (h ^ (h >> 13)) * 1274126177;
      return ((h ^ (h >> 16)) & 0xffff) / 0xffff;
    };
    for (let py = 0; py < H; py++) {
      const wy = py / ss;
      for (let px = 0; px < W; px++) {
        const wx = px / ss;
        const e = m.elevationAt(wx, wy);
        // Directional (NW light) slope shading from a small world-space delta.
        const eN = m.elevationAt(wx - 3, wy - 3);
        const slope = (e - eN) * 90;
        // Low-frequency tonal variation (large soft blotches).
        const lf =
          Math.sin(wx * 0.018 + wy * 0.011) * 5 +
          Math.sin(wx * 0.006 - wy * 0.014 + 2.1) * 6;
        // Fine grain.
        const grain = (hash(px, py) - 0.5) * 11;
        const base = 24 + e * 60 + lf;
        const rr = base * 0.7 + slope + 8 + grain;
        const gg = base * 0.85 + slope + 12 + grain;
        const bb = base * 1.0 + slope + 18 + grain;
        const i = (py * W + px) * 4;
        data[i] = rr < 0 ? 0 : rr > 255 ? 255 : rr;
        data[i + 1] = gg < 0 ? 0 : gg > 255 ? 255 : gg;
        data[i + 2] = bb < 0 ? 0 : bb > 255 ? 255 : bb;
        data[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);

    // Soft contour bands: a faint highlight line where the terrain crosses each
    // elevation level. Drawn as short segments along the level set (not blobs).
    g.save();
    g.scale(ss, ss);
    const cs = m.cellSize;
    const bands = [0.2, 0.35, 0.5, 0.65, 0.8];
    g.lineCap = "round";
    for (const b of bands) {
      g.strokeStyle = "rgba(200,228,248,0.07)";
      g.lineWidth = 1.1;
      g.beginPath();
      for (let r = 0; r < m.rows - 1; r++) {
        for (let col = 0; col < m.cols - 1; col++) {
          const h0 = m.heights[r * m.cols + col];
          const h1 = m.heights[r * m.cols + col + 1];
          const h2 = m.heights[(r + 1) * m.cols + col];
          const cx = col * cs + cs / 2;
          const cy = r * cs + cs / 2;
          // Horizontal crossing (between this cell and the one to the right).
          if ((h0 < b) !== (h1 < b)) { g.moveTo(cx + cs * 0.5, cy - cs * 0.35); g.lineTo(cx + cs * 0.5, cy + cs * 0.35); }
          // Vertical crossing (between this cell and the one below).
          if ((h0 < b) !== (h2 < b)) { g.moveTo(cx - cs * 0.35, cy + cs * 0.5); g.lineTo(cx + cs * 0.35, cy + cs * 0.5); }
        }
      }
      g.stroke();
    }
    g.lineCap = "butt";
    // Baked metal-spot craters (subtle scar + rim), so spots read even far out.
    for (const s of m.def.metalSpots) {
      const rad = 16;
      const grd = g.createRadialGradient(s.x, s.y, 0, s.x, s.y, rad);
      grd.addColorStop(0, "rgba(40,34,20,0.55)");
      grd.addColorStop(0.7, "rgba(60,52,30,0.25)");
      grd.addColorStop(1, "rgba(60,52,30,0)");
      g.fillStyle = grd;
      g.beginPath(); g.arc(s.x, s.y, rad, 0, 7); g.fill();
    }
    g.restore();
    return c;
  }

  render(alpha: number, view: RenderView): void {
    const ctx = this.ctx;
    const cam = this.camera;
    const w = this.sim.world;
    const dpr = this.dpr;
    const shx = this.fx.shakeX;
    const shy = this.fx.shakeY;

    // Base screen transform (no shake) for the backdrop + vignette.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.drawBackdrop(ctx, cam);

    // World layer transform (with screen shake folded in).
    const setWorld = (): void => {
      ctx.setTransform(
        dpr * cam.zoom, 0, 0, dpr * cam.zoom,
        dpr * (cam.screenW / 2 - cam.x * cam.zoom + shx),
        dpr * (cam.screenH / 2 - cam.y * cam.zoom + shy),
      );
    };
    const setScreen = (): void => {
      ctx.setTransform(dpr, 0, 0, dpr, dpr * shx, dpr * shy);
    };

    // --- Terrain ---
    setWorld();
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.terrain, 0, 0, w.gmap.worldW, w.gmap.worldH);
    setScreen();

    // Border treatment around the play area (glow + inner darkening).
    this.drawMapBorder(ctx, cam);

    // Scorch decals sit on the ground, under everything.
    this.fx.drawGround(ctx, cam);

    this.drawMetalSpots(ctx, view);

    // Subtle build grid only while placing.
    if (view.placing) this.drawBuildGrid(ctx, cam);

    const view2 = cam.viewRect();
    const pad = 60;
    const iconZoom = cam.isIconZoom();

    // --- Entities, back-to-front by kind (features/wreckage under units) ---
    for (const passKind of [KIND_FEATURE, KIND_WRECKAGE, KIND_BUILDING, KIND_UNIT]) {
      for (let e = 0; e < w.count; e++) {
        if (!w.alive[e] || w.kind[e] !== passKind) continue;
        const ix = passKind === KIND_UNIT ? lerp(w.prevX[e], w.posX[e], alpha) : w.posX[e];
        const iy = passKind === KIND_UNIT ? lerp(w.prevY[e], w.posY[e], alpha) : w.posY[e];
        if (ix < view2.x0 - pad || ix > view2.x1 + pad || iy < view2.y0 - pad || iy > view2.y1 + pad) continue;
        this.drawEntity(e, ix, iy, alpha, iconZoom, view);
      }
    }

    // Nanolathe build beams (constructor → ghost).
    this.drawNanolathe(ctx, cam, alpha);
    // Factory rally lines for selected factories.
    this.drawRallyLines(ctx, cam, view);

    // --- Projectiles ---
    this.drawProjectiles(ctx, cam, alpha);

    // --- FX (explosions, tracers, debris, order markers) ---
    this.fx.draw(ctx, cam);

    // --- Placement ghost ---
    if (view.placing) this.drawPlacement(ctx, cam, view.placing);

    // --- Selection box ---
    if (view.selBox) {
      const b = view.selBox;
      ctx.strokeStyle = "rgba(130,240,175,0.95)";
      ctx.fillStyle = "rgba(120,230,160,0.10)";
      ctx.lineWidth = 1.5;
      const x = Math.min(b.x0, b.x1), y = Math.min(b.y0, b.y1);
      const bw = Math.abs(b.x1 - b.x0), bh = Math.abs(b.y1 - b.y0);
      ctx.fillRect(x, y, bw, bh);
      ctx.strokeRect(x, y, bw, bh);
    }

    // Full-screen vignette (never shakes).
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawVignette(ctx, cam);
  }

  private drawBackdrop(ctx: CanvasRenderingContext2D, cam: Camera): void {
    // Deep ops-table void: radial gradient + faint world-aligned grid so the
    // battlefield sits in a designed space, never on raw black.
    const g = ctx.createRadialGradient(
      cam.screenW / 2, cam.screenH / 2, 0,
      cam.screenW / 2, cam.screenH / 2, Math.hypot(cam.screenW, cam.screenH) / 2,
    );
    g.addColorStop(0, "#0a1017");
    g.addColorStop(1, "#04060a");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cam.screenW, cam.screenH);

    // World-aligned grid (visible mostly in the void; terrain covers the field).
    const step = 96;
    const v = cam.viewRect();
    ctx.strokeStyle = "rgba(90,120,150,0.05)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    const x0 = Math.floor(v.x0 / step) * step;
    for (let x = x0; x <= v.x1; x += step) {
      const sx = cam.worldToScreenX(x);
      ctx.moveTo(sx, 0); ctx.lineTo(sx, cam.screenH);
    }
    const y0 = Math.floor(v.y0 / step) * step;
    for (let y = y0; y <= v.y1; y += step) {
      const sy = cam.worldToScreenY(y);
      ctx.moveTo(0, sy); ctx.lineTo(cam.screenW, sy);
    }
    ctx.stroke();
  }

  private drawMapBorder(ctx: CanvasRenderingContext2D, cam: Camera): void {
    const m = this.sim.world.gmap;
    const x0 = cam.worldToScreenX(0), y0 = cam.worldToScreenY(0);
    const x1 = cam.worldToScreenX(m.worldW), y1 = cam.worldToScreenY(m.worldH);
    const bw = x1 - x0, bh = y1 - y0;
    ctx.save();
    // Outer glow line.
    ctx.strokeStyle = "rgba(120,170,205,0.5)";
    ctx.shadowColor = "rgba(90,150,200,0.6)";
    ctx.shadowBlur = 14;
    ctx.lineWidth = 2;
    ctx.strokeRect(x0, y0, bw, bh);
    ctx.shadowBlur = 0;
    // Inner edge darkening so the field reads as placed, not cropped.
    ctx.beginPath();
    ctx.rect(x0, y0, bw, bh);
    ctx.clip();
    const edge = Math.min(bw, bh) * 0.06;
    const grads: [number, number, number, number, number, number][] = [
      [x0, y0, x0 + edge, y0, 1, 0],
      [x1, y0, x1 - edge, y0, 1, 0],
      [x0, y0, x0, y0 + edge, 0, 1],
      [x0, y1, x0, y1 - edge, 0, 1],
    ];
    for (const [gx0, gy0, gx1, gy1] of grads) {
      const lg = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
      lg.addColorStop(0, "rgba(2,4,7,0.55)");
      lg.addColorStop(1, "rgba(2,4,7,0)");
      ctx.fillStyle = lg;
      ctx.fillRect(x0, y0, bw, bh);
    }
    ctx.restore();
  }

  private drawVignette(ctx: CanvasRenderingContext2D, cam: Camera): void {
    const g = ctx.createRadialGradient(
      cam.screenW / 2, cam.screenH / 2, Math.min(cam.screenW, cam.screenH) * 0.42,
      cam.screenW / 2, cam.screenH / 2, Math.max(cam.screenW, cam.screenH) * 0.75,
    );
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(1, "rgba(0,0,0,0.42)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cam.screenW, cam.screenH);
  }

  private drawBuildGrid(ctx: CanvasRenderingContext2D, cam: Camera): void {
    // A subtle placement grid, only shown while a building is being placed.
    const step = 24;
    ctx.strokeStyle = "rgba(120,200,235,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    const v = cam.viewRect();
    const sx0 = Math.floor(v.x0 / step) * step;
    for (let x = sx0; x <= v.x1; x += step) {
      const s = cam.worldToScreenX(x);
      ctx.moveTo(s, 0); ctx.lineTo(s, cam.screenH);
    }
    const sy0 = Math.floor(v.y0 / step) * step;
    for (let y = sy0; y <= v.y1; y += step) {
      const s = cam.worldToScreenY(y);
      ctx.moveTo(0, s); ctx.lineTo(cam.screenW, s);
    }
    ctx.stroke();
  }

  private drawMetalSpots(ctx: CanvasRenderingContext2D, view: RenderView): void {
    const cam = this.camera;
    const w = this.sim.world;
    const spots = w.gmap.def.metalSpots;
    const t = performance.now() * 0.002;
    for (let i = 0; i < spots.length; i++) {
      const sx = cam.worldToScreenX(spots[i].x);
      const sy = cam.worldToScreenY(spots[i].y);
      const occupied = w.metalSpotOccupant[i] >= 0;
      const r = 8 * Math.max(0.8, Math.min(1.7, cam.zoom));
      if (occupied) {
        // Occupied: quiet grey seam.
        ctx.strokeStyle = "rgba(150,160,170,0.4)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(sx, sy - r); ctx.lineTo(sx + r, sy); ctx.lineTo(sx, sy + r); ctx.lineTo(sx - r, sy); ctx.closePath();
        ctx.stroke();
        continue;
      }
      // Free spot: glowing golden crater/seam that pulses.
      const pulse = 0.6 + 0.4 * Math.sin(t + i);
      const grd = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 1.6);
      grd.addColorStop(0, `rgba(255,205,90,${0.32 * pulse})`);
      grd.addColorStop(0.6, `rgba(255,180,60,${0.14 * pulse})`);
      grd.addColorStop(1, "rgba(255,180,60,0)");
      ctx.fillStyle = grd;
      ctx.beginPath(); ctx.arc(sx, sy, r * 1.6, 0, 7); ctx.fill();
      ctx.strokeStyle = `rgba(255,215,110,${0.7 + 0.3 * pulse})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sx, sy - r); ctx.lineTo(sx + r, sy); ctx.lineTo(sx, sy + r); ctx.lineTo(sx - r, sy); ctx.closePath();
      ctx.stroke();
      // Inner core.
      ctx.fillStyle = `rgba(255,235,170,${0.5 * pulse})`;
      ctx.beginPath(); ctx.arc(sx, sy, r * 0.28, 0, 7); ctx.fill();
      if (view.placing) {
        ctx.strokeStyle = "rgba(255,215,110,0.9)";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(sx, sy, r * 1.9, 0, 7); ctx.stroke();
      }
    }
  }

  private drawEntity(e: number, ix: number, iy: number, alpha: number, iconZoom: boolean, view: RenderView): void {
    const ctx = this.ctx;
    const cam = this.camera;
    const w = this.sim.world;
    const def = w.def(e);
    const sx = cam.worldToScreenX(ix);
    const sy = cam.worldToScreenY(iy);
    const owner = w.owner[e];
    const selected = view.selection.has(e);
    const kind = w.kind[e];
    const uc = (w.flags[e] & F_UNDER_CONSTRUCTION) !== 0;
    const healthFrac = Math.max(0, Math.min(1, w.health[e] / w.maxHealth[e]));
    const t = performance.now() * 0.001;

    // Soft ground shadow (grounds the unit on the terrain).
    if (!iconZoom) {
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.beginPath();
      ctx.ellipse(sx, sy + (def.radius * 0.35) * cam.zoom, (def.radius * 0.9) * cam.zoom, (def.radius * 0.46) * cam.zoom, 0, 0, 7);
      ctx.fill();
    }

    // Selection marker (below sprite) with a soft pulse.
    if (selected) {
      const pr = (def.radius + 4) * cam.zoom + Math.sin(t * 4) * 1.2;
      ctx.strokeStyle = "#b6ffce";
      ctx.globalAlpha = 0.85 + 0.15 * Math.sin(t * 4);
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(sx, sy, pr, 0, 7); ctx.stroke();
      ctx.globalAlpha = 1;
    } else if (view.hovered === e) {
      ctx.strokeStyle = "rgba(200,220,255,0.5)";
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(sx, sy, (def.radius + 4) * cam.zoom, 0, 7); ctx.stroke();
    }

    if (kind === KIND_FEATURE) {
      ctx.save();
      ctx.translate(sx, sy);
      ctx.scale(cam.zoom, cam.zoom);
      drawSprite(ctx, def.role, "neutral", def.radius, this.palPlayer);
      ctx.restore();
      return;
    }
    if (kind === KIND_WRECKAGE) {
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(w.heading[e]);
      ctx.scale(cam.zoom, cam.zoom);
      ctx.globalAlpha = 0.55;
      ctx.filter = "grayscale(0.6) brightness(0.5)";
      drawSprite(ctx, def.role, this.ownerFaction(owner === 0 ? OWNER_PLAYER : owner), def.radius, this.ownerPalette(owner === 0 ? OWNER_PLAYER : owner));
      ctx.filter = "none";
      ctx.globalAlpha = 1;
      ctx.restore();
      return;
    }

    const heading = kind === KIND_UNIT ? lerpAngle(w.prevHeading[e], w.heading[e], alpha) : w.heading[e];

    if (iconZoom && !uc) {
      // Strategic icon.
      const s = Math.max(4, (def.radius + (def.role === "commander" ? 6 : 2)) * 0.9);
      ctx.save();
      ctx.translate(sx, sy);
      drawIcon(ctx, def.role, s, this.ownerColor(owner));
      ctx.restore();
      if (healthFrac < 1) {
        ctx.strokeStyle = healthFrac > 0.5 ? "#5bd66a" : healthFrac > 0.25 ? "#e0c840" : "#e5533b";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sx, sy, s + 3, -Math.PI / 2, -Math.PI / 2 + healthFrac * Math.PI * 2);
        ctx.stroke();
      }
      return;
    }

    // Full sprite. Prefer the pre-rendered 3D atlas (heading snapped to the
    // nearest of 8 baked yaw frames — the authentic snap-to-8 look); fall back
    // to the procedural vector painter if sprites are off or unavailable.
    const ucAlpha = uc ? 0.35 + 0.5 * w.buildProgress[e] : 1;
    const useSprite = settings.unitArt === "sprite" && spriteAtlas.has(def.id);
    if (useSprite) {
      spriteAtlas.drawUnit(ctx, def.id, sx, sy, heading, cam.zoom, ucAlpha);
    } else {
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(heading);
      ctx.scale(cam.zoom, cam.zoom);
      if (uc) ctx.globalAlpha = ucAlpha;
      drawSprite(ctx, def.role, this.ownerFaction(owner), def.radius, this.ownerPalette(owner));
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // Player-colour underglow ring for identification.
    ctx.strokeStyle = this.ownerColor(owner);
    ctx.globalAlpha = 0.32;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(sx, sy, (def.radius + 1) * cam.zoom, 0, 7);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Muzzle flash (cheap layered arcs — no per-frame gradient alloc).
    if (w.fireFlash[e] > 0 && def.weapon) {
      const bx = sx + Math.cos(heading) * (def.radius + 3) * cam.zoom;
      const by = sy + Math.sin(heading) * (def.radius + 3) * cam.zoom;
      const fa = w.fireFlash[e] / 0.09;
      const fr = Math.max(2.5, def.radius * 0.5 * cam.zoom);
      ctx.globalAlpha = fa * 0.5;
      ctx.fillStyle = "#ffcf78";
      ctx.beginPath(); ctx.arc(bx, by, fr * 1.7, 0, 7); ctx.fill();
      ctx.globalAlpha = fa;
      ctx.fillStyle = "#fff8d6";
      ctx.beginPath(); ctx.arc(bx, by, fr * 0.7, 0, 7); ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Construction progress arc.
    if (uc) {
      ctx.strokeStyle = "#7fd4ff";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(sx, sy, (def.radius + 4) * cam.zoom, -Math.PI / 2, -Math.PI / 2 + w.buildProgress[e] * Math.PI * 2);
      ctx.stroke();
    }

    // Health bar (when damaged or selected).
    if ((healthFrac < 1 || selected) && !uc) {
      const bw = (def.radius * 2.2) * cam.zoom;
      const bh = Math.max(2.5, 3 * cam.zoom * 0.6);
      const bx = sx - bw / 2;
      const by = sy - (def.radius + 7) * cam.zoom;
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(bx - 1, by - 1, bw + 2, bh + 2);
      ctx.fillStyle = healthFrac > 0.5 ? "#5bd66a" : healthFrac > 0.25 ? "#e0c840" : "#e5533b";
      ctx.fillRect(bx, by, bw * healthFrac, bh);
    }
  }

  private drawNanolathe(ctx: CanvasRenderingContext2D, cam: Camera, alpha: number): void {
    const w = this.sim.world;
    const t = performance.now() * 0.001;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.kind[e] !== KIND_UNIT) continue;
      // Only while actively building/repairing/reclaiming this tick (buildTargetEnt
      // is not cleared when the builder stops, so gate on the live action state).
      const act = w.action[e];
      if (act !== ACT_BUILD && act !== ACT_RECLAIM && act !== ACT_REPAIR) continue;
      const tgt = w.buildTargetEnt[e];
      if (tgt < 0 || !w.isAlive(tgt)) continue;
      const reclaim = act === ACT_RECLAIM;
      const bx = cam.worldToScreenX(lerp(w.prevX[e], w.posX[e], alpha));
      const by = cam.worldToScreenY(lerp(w.prevY[e], w.posY[e], alpha));
      const gx = cam.worldToScreenX(w.posX[tgt]);
      const gy = cam.worldToScreenY(w.posY[tgt]);
      const col = reclaim ? "#ffc24d" : act === ACT_REPAIR ? "#7cf0d0" : this.ownerColor(w.owner[e]);
      // Beam.
      ctx.strokeStyle = col;
      ctx.globalAlpha = 0.35 + 0.15 * Math.sin(t * 20);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.lineDashOffset = -t * 30;
      ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(gx, gy); ctx.stroke();
      ctx.setLineDash([]);
      // Spray particles converging on the ghost.
      ctx.globalAlpha = 0.8;
      ctx.fillStyle = col;
      for (let k = 0; k < 3; k++) {
        const f = ((t * 2 + k * 0.33) % 1);
        const x = lerp(bx, gx, f), y = lerp(by, gy, f);
        ctx.beginPath(); ctx.arc(x, y, 1.6, 0, 7); ctx.fill();
      }
      // Shimmer on the ghost.
      const gr = (w.def(tgt).radius + 2) * cam.zoom;
      ctx.globalAlpha = 0.25 + 0.2 * Math.sin(t * 12);
      ctx.strokeStyle = col;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(gx, gy, gr, 0, 7); ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  private drawRallyLines(ctx: CanvasRenderingContext2D, cam: Camera, view: RenderView): void {
    const w = this.sim.world;
    for (const e of view.selection) {
      if (!w.isAlive(e) || w.def(e).role !== "factory") continue;
      const rx = w.rallyX[e], ry = w.rallyY[e];
      if (Math.abs(rx - w.posX[e]) < 2 && Math.abs(ry - w.posY[e]) < 2) continue;
      const x0 = cam.worldToScreenX(w.posX[e]), y0 = cam.worldToScreenY(w.posY[e]);
      const x1 = cam.worldToScreenX(rx), y1 = cam.worldToScreenY(ry);
      ctx.strokeStyle = "rgba(130,240,175,0.6)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 5]);
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(130,240,175,0.8)";
      ctx.beginPath(); ctx.arc(x1, y1, 3.5, 0, 7); ctx.fill();
    }
  }

  private drawProjectiles(ctx: CanvasRenderingContext2D, cam: Camera, alpha: number): void {
    const p = this.sim.proj;
    const heightScale = 0.5; // visual lift for arc height
    for (let i = 0; i < p.count; i++) {
      if (!p.alive[i]) continue;
      const x = lerp(p.px[i], p.x[i], alpha);
      const y = lerp(p.py[i], p.y[i], alpha);
      const z = lerp(p.pz[i], p.z[i], alpha);
      const fac = p.faction[i];
      const col = fac === 1 ? "#ffb066" : "#8be9ff";
      if (p.ptype[i] === PROJ_ARC) {
        const sx = cam.worldToScreenX(x);
        const syGround = cam.worldToScreenY(y);
        const sy = syGround - z * heightScale * cam.zoom;
        ctx.fillStyle = "rgba(0,0,0,0.3)";
        ctx.beginPath();
        ctx.ellipse(sx, syGround, 3 * cam.zoom, 1.5 * cam.zoom, 0, 0, 7);
        ctx.fill();
        // Glowing shell: soft halo + bright core (cheap layered arcs).
        ctx.globalAlpha = 0.4;
        ctx.fillStyle = col;
        ctx.beginPath(); ctx.arc(sx, sy, 5 * cam.zoom, 0, 7); ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath(); ctx.arc(sx, sy, 2 * cam.zoom, 0, 7); ctx.fill();
      } else {
        // Bolt tracer: bright glow line + white core.
        const len = 0.06;
        const x0 = x - p.vx[i] * len;
        const y0 = y - p.vy[i] * len;
        const sx0 = cam.worldToScreenX(x0), sy0 = cam.worldToScreenY(y0);
        const sx1 = cam.worldToScreenX(x), sy1 = cam.worldToScreenY(y);
        ctx.lineCap = "round";
        ctx.strokeStyle = col;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = 3.5 * cam.zoom;
        ctx.beginPath(); ctx.moveTo(sx0, sy0); ctx.lineTo(sx1, sy1); ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = Math.max(1, 1.4 * cam.zoom);
        ctx.beginPath(); ctx.moveTo(sx0, sy0); ctx.lineTo(sx1, sy1); ctx.stroke();
        ctx.lineCap = "butt";
      }
    }
  }

  private drawPlacement(ctx: CanvasRenderingContext2D, cam: Camera, placing: { defId: string; x: number; y: number; snap: { magnetized: boolean; nx: number; ny: number } | null }): void {
    const w = this.sim.world;
    const def = w.def(w.defIndex(placing.defId));
    const legal = canPlace(w, placing.defId, placing.x, placing.y);
    const sx = cam.worldToScreenX(placing.x);
    const sy = cam.worldToScreenY(placing.y);

    // Snap feedback: magnetize guide (flush-to-neighbour) or a subtle grid tick.
    const snap = placing.snap;
    if (snap && legal) {
      if (snap.magnetized) {
        const nsx = cam.worldToScreenX(snap.nx);
        const nsy = cam.worldToScreenY(snap.ny);
        // Alignment guide from the neighbour's centre to the ghost, plus an edge tick.
        ctx.strokeStyle = "rgba(124,240,208,0.9)";
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(nsx, nsy); ctx.lineTo(sx, sy); ctx.stroke();
        ctx.setLineDash([]);
        // A short bright bar across the shared edge (perpendicular to the guide).
        const dx = sx - nsx, dy = sy - nsy;
        const m = Math.hypot(dx, dy) || 1;
        const tx = -dy / m, ty = dx / m;
        const midx = (sx + nsx) / 2, midy = (sy + nsy) / 2;
        const half = (def.radius + 2) * cam.zoom;
        ctx.strokeStyle = "rgba(150,255,220,0.95)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(midx - tx * half, midy - ty * half);
        ctx.lineTo(midx + tx * half, midy + ty * half);
        ctx.stroke();
      } else {
        // Grid-snapped: faint alignment crosshair through the ghost centre.
        const r = (def.radius + 8) * cam.zoom;
        ctx.strokeStyle = "rgba(124,240,208,0.35)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx - r, sy); ctx.lineTo(sx + r, sy);
        ctx.moveTo(sx, sy - r); ctx.lineTo(sx, sy + r);
        ctx.stroke();
      }
    }

    ctx.save();
    ctx.translate(sx, sy);
    ctx.scale(cam.zoom, cam.zoom);
    ctx.globalAlpha = 0.5;
    drawSprite(ctx, def.role, this.facPlayer, def.radius, this.palPlayer);
    ctx.restore();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = legal ? "rgba(120,230,160,0.95)" : "rgba(255,80,60,0.95)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(sx, sy, (def.radius + 3) * cam.zoom, 0, 7);
    ctx.stroke();
    if (!legal) {
      ctx.beginPath();
      ctx.moveTo(sx - def.radius * cam.zoom, sy - def.radius * cam.zoom);
      ctx.lineTo(sx + def.radius * cam.zoom, sy + def.radius * cam.zoom);
      ctx.stroke();
    }
    if (def.weapon) {
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(sx, sy, def.weapon.range * cam.zoom, 0, 7);
      ctx.stroke();
    }
  }
}
